import { Navigate, type LoaderFunctionArgs, type RouteObject } from 'react-router';
import { getParentChildReportTemplatesQueryOptions } from '~/hooks/queries/useParentChildReportTemplatesQuery';
import { getStaffStatColumnCatalogQueryOptions } from '~/hooks/queries/useStaffStatColumnCatalogQuery';
import { getStaffStatsAccessQueryOptions } from '~/hooks/queries/useStaffStatsAccessQuery';
import { globalClient } from '~/query-client';
import { throwRedirectForAnalyticsReportingModeSearch } from '~/routing/analyticsReportingModeSearchRedirect';
import { perms } from '~/routing/perms';
import { prefetchQueryNonBlocking } from '~/routing/prefetchQueryNonBlocking';
import { requirePerm } from '~/routing/requirePerm';
import { getSessionPlQueryOptions } from '~/routing/sessionPlQueryOptions';
import {
  ANALYTICS_REPORTING_MODE_SEARCH_PARAM,
  getAnalyticsReportingMode,
  isGenie2ReportingMode,
  parseGenie2ReportingModeSearchParam,
  usesAnalyticsStatsUi,
  type Genie2ReportingMode,
} from '~/utils/analyticsReportingMode';
import StaffClientReportTemplatesPage from '~/views/Staff/StatLimits/StaffClientReportTemplatesPage';
import StaffUnifiedColumnCatalogPage from '~/views/Staff/StatLimits/StaffUnifiedColumnCatalogPage';
import ParentAnalyticsAccessShell from './ParentAnalyticsAccessShell';

async function resolveParentChildConfigScope(
  parentAccountId: string,
  childClientId: string,
  request: Request,
) {
  try {
    const access = await globalClient.fetchQuery(
      getStaffStatsAccessQueryOptions('client', childClientId, {
        parentAccountId,
      }),
    );
    const persisted = access.analyticsReportingMode;

    let parentGenie2Mode: Genie2ReportingMode | null = null;
    if (persisted === 'legacy') {
      const session = await globalClient.ensureQueryData(
        getSessionPlQueryOptions({ accountId: parentAccountId, accountType: 'client' }),
      );
      const parentMode = getAnalyticsReportingMode(session);
      parentGenie2Mode = isGenie2ReportingMode(parentMode) ? parentMode : null;
    }

    throwRedirectForAnalyticsReportingModeSearch({
      requestUrl: request.url,
      persistedMode: persisted,
      parentGenie2Mode,
    });

    if (persisted != null && usesAnalyticsStatsUi(persisted)) {
      return { analyticsReportingMode: null, canPrefetch: true };
    }
    const fromSearch = parseGenie2ReportingModeSearchParam(
      new URL(request.url).searchParams.get(ANALYTICS_REPORTING_MODE_SEARCH_PARAM),
    );
    if (fromSearch != null) {
      return { analyticsReportingMode: fromSearch, canPrefetch: true };
    }
    return { analyticsReportingMode: null, canPrefetch: false };
  } catch (e) {
    if (e instanceof Response) {
      throw e;
    }
    // Let the shell / page queries surface the error instead of aborting navigation.
    return { analyticsReportingMode: null, canPrefetch: false };
  }
}

/** Prefetch child access via parent → children mount while session stays on the parent URL. */
const parentColumnAccessLoader = async (ctx: LoaderFunctionArgs) => {
  await requirePerm(perms.parentChildColumnAccess)(ctx);
  const { accountId, childClientId } = ctx.params;
  if (accountId && childClientId) {
    const scope = await resolveParentChildConfigScope(accountId, childClientId, ctx.request);
    prefetchQueryNonBlocking(
      globalClient,
      getStaffStatsAccessQueryOptions('client', childClientId, {
        parentAccountId: accountId,
        analyticsReportingMode: scope.analyticsReportingMode,
      }),
    );
    if (scope.canPrefetch) {
      prefetchQueryNonBlocking(
        globalClient,
        getStaffStatColumnCatalogQueryOptions('client', childClientId, {
          parentAccountId: accountId,
          analyticsReportingMode: scope.analyticsReportingMode,
        }),
      );
    }
  }
  return null;
};

const parentReportTemplatesLoader = async (ctx: LoaderFunctionArgs) => {
  await requirePerm(perms.parentChildColumnAccess)(ctx);
  const { accountId, childClientId } = ctx.params;
  if (accountId && childClientId) {
    const scope = await resolveParentChildConfigScope(accountId, childClientId, ctx.request);
    if (scope.canPrefetch) {
      prefetchQueryNonBlocking(
        globalClient,
        getParentChildReportTemplatesQueryOptions(
          accountId,
          childClientId,
          undefined,
          scope.analyticsReportingMode,
        ),
      );
    }
  }
  return null;
};

/** Nested under `/account/child-accounts/:childClientId/…` so parent session stays in the URL. */
export const parentChildColumnAccessChildren: RouteObject[] = [
  {
    path: ':childClientId/analytics-access',
    element: <ParentAnalyticsAccessShell />,
    loader: parentColumnAccessLoader,
    shouldRevalidate: ({ currentUrl, nextUrl }) =>
      currentUrl.pathname !== nextUrl.pathname ||
      currentUrl.searchParams.get(ANALYTICS_REPORTING_MODE_SEARCH_PARAM) !==
        nextUrl.searchParams.get(ANALYTICS_REPORTING_MODE_SEARCH_PARAM),
    children: [
      { index: true, element: <Navigate to="columns" replace /> },
      {
        path: 'columns',
        element: <StaffUnifiedColumnCatalogPage />,
      },
      {
        path: 'report-templates',
        element: <StaffClientReportTemplatesPage />,
        loader: parentReportTemplatesLoader,
      },
    ],
  },
];
